import { EOtherIncomeType } from '@if/core/types'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import { Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useTranslation } from 'react-i18next'
import ClickableAddress from '../../../components/AddressWithGoogleSearch'
import { useCurrentLang, useJobTypeEnum, useOtherIncomeTypesEnum } from '../../../data/selectors/app-config-selectors'
import { ApplicantJob, ApplicantOtherIncome, CreditApplicationTask } from '../../../data/types'
import PhoneNumber from './PhoneNumber'
import TaskStatus from './TaskStatus'

interface Props {
  incomes: ApplicantJob[]
  otherIncomes: ApplicantOtherIncome[]
  jobTasks: CreditApplicationTask[]
  otherIncomeTasks: CreditApplicationTask[]
}

const IncomeInformationTable = ({ incomes, otherIncomes, jobTasks, otherIncomeTasks }: Props) => {
  const { t } = useTranslation()
  const { creditApplication } = useCreditApplicationData()
  const financingProgramId = creditApplication?.financingProgramId
  const jobTypeEnum = useJobTypeEnum(financingProgramId || null)
  const otherIncomeTypeEnum = useOtherIncomeTypesEnum()
  const lang = useCurrentLang()

  let totalIncome = 0
  let totalOtherIncome = 0
  incomes.forEach((item) => {
    totalIncome += item.annualSalary
  })

  otherIncomes.forEach((item) => {
    totalOtherIncome += item.annualAmount
  })

  return (
    <TableContainer component={Paper}>
      <Table aria-label="spanning table">
        <TableBody>
          {incomes.length !== 0 && (
            <TableRow>
              <TableCell colSpan={3}>
                <Typography variant="h6" component="h6">
                  {t('editCreditApplication.income.job.jobs')}:
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {incomes.map((item) => (
            <TableRow key={item.id}>
              <TableCell>
                <Typography>{`${item.employerName} (${jobTypeEnum[item.jobType.toString()][lang]}) : ${
                  item.jobTitle
                }`}</Typography>
                <Typography component="div">
                  {t('credit.address')}: <ClickableAddress address={item.address} component="span" />
                </Typography>

                <PhoneNumber
                  phone={item.employerPhone || ''}
                  phoneExt={item.employerPhoneExt || ''}
                  applyPhoneFormatting
                  enableGoogleSearch={true}
                />
              </TableCell>
              <TableCell align="right">{formatCurrencyNoDecimals(item.annualSalary)}</TableCell>
              <TableCell align="right" width="5%">
                <TaskStatus task={jobTasks.find((x) => x.subKey === item.id)} />
              </TableCell>
            </TableRow>
          ))}
          {otherIncomes.length !== 0 && (
            <TableRow>
              <TableCell colSpan={3}>
                <Typography variant="h6" component="h6">
                  {t('editCreditApplication.income.other.label')}:
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {otherIncomes.map((item) => (
            <TableRow key={item.id}>
              <TableCell>
                {otherIncomeTypeEnum[item.typeId.toString()][lang]}
                {item.typeId === EOtherIncomeType.Other && ` : ${item.description}`}
              </TableCell>
              <TableCell align="right">{formatCurrencyNoDecimals(item.annualAmount)}</TableCell>
              <TableCell align="right" width="5%">
                <TaskStatus task={otherIncomeTasks.find((x) => x.subKey === item.id)} />
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell>
              <Typography variant="h6" component="h6">
                {t('credit.totalIncomes')}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="h6" component="h6">
                {formatCurrencyNoDecimals(totalIncome + totalOtherIncome)}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default IncomeInformationTable
